<template>
  <Layout padding-horizontal="0" padding-vertical="0">
    <div class="reports-wrapper">
      <div class="page-header">
        <div class="page-title">
          <h2>{{$t('reports.reports_title')}}</h2>
          <h5>{{$t('reports.manage_and_view')}}</h5>
        </div>
        <div class="actions" />
      </div>

      <div class="main-content">
        <div class="tab-panel-row">
          <div class="tab-panel">
            <button class="tab-button" :class="{'active': mainTabIndex === 0}" @click="() => changeMainTab(0)">
              <span>{{ $t('tests_tab') }}</span>
            </button>
            <button class="tab-button" :class="{'active': mainTabIndex === 1}" @click="() => changeMainTab(1)">
              <span>{{ $t('programs_tab') }}</span>
            </button>
            <button class="tab-button" :class="{'active': mainTabIndex === 2}" @click="() => changeMainTab(2)">
              <span>{{ $t('users_tab') }}</span>
            </button>
            <button class="tab-button" :class="{'active': mainTabIndex === 3}" @click="() => changeMainTab(3)">
              <span>{{ $t('game_labs_tab') }}</span>
            </button>
            <button class="tab-button" :class="{'active': mainTabIndex === 4}" @click="() => changeMainTab(4)">
              <span>{{ $t('programAnalytics.title') }}</span>
            </button>
          </div>
          <!-- Program Analitiği sekmesinde geçmiş/PDF rapor akışı yok -->
          <div class="custom-report" v-if="mainTabIndex !== 4">
            <button class="tab-button" :class="{'active': showCustomReport}" @click="toggleCustomReport">
                <span>
                  <template v-if="showCustomReport">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 2C11.3138 2 14 4.6862 14 8C14 11.3138 11.3138 14 8 14C4.6862 14 2 11.3138 2 8H3.2C3.2 10.6508 5.3492 12.8 8 12.8C10.6508 12.8 12.8 10.6508 12.8 8C12.8 5.3492 10.6508 3.2 8 3.2C6.5216 3.2 5.1992 3.8684 4.319 4.919L5.6 6.2H2V2.6L3.4682 4.0676C4.568 2.8016 6.1904 2 8 2ZM8.6 5V7.751L10.5458 9.6968L9.6968 10.5458L7.4 8.2478V5H8.6Z" fill="#344054"/>
                    </svg>
                    {{$t('reports.past_reports')}}
                  </template>
                  <template v-else>
                    {{$t('reports.generated_report')}}
                  </template>
                </span>
            </button>
          </div>
        </div>
      </div>

      <div v-if="!showCustomReport" style="padding: 40px">
        <TabContent
            v-if="mainTabIndex === 0"
            tabType="test"
            :selectedTime="selectedTime"
            :isPdfGenerating="isPdfGenerating"
            @update:selectedTime="updateSelectedTime"
            @viewTabChanged="handleViewTabChange"
            @reportRequested="handleReportRequest"
            @chartDownloadRequested="handleChartDownload"
            @chartDataUpdated="handleChartDataUpdate"
        ></TabContent>

        <TabContent
            v-if="mainTabIndex === 1"
            tabType="program"
            :selectedTime="selectedTime"
            :isPdfGenerating="isPdfGenerating"
            @update:selectedTime="updateSelectedTime"
            @viewTabChanged="handleViewTabChange"
            @reportRequested="handleReportRequest"
            @chartDownloadRequested="handleChartDownload"
            @chartDataUpdated="handleChartDataUpdate"
        ></TabContent>

        <TabContent
            v-if="mainTabIndex === 2"
            tabType="user"
            :selectedTime="selectedTime"
            :isPdfGenerating="isPdfGenerating"
            @update:selectedTime="updateSelectedTime"
            @viewTabChanged="handleViewTabChange"
            @reportRequested="handleReportRequest"
            @chartDownloadRequested="handleChartDownload"
            @chartDataUpdated="handleChartDataUpdate"
        ></TabContent>

        <TabContent
            v-if="mainTabIndex === 3"
            tabType="gameLabs"
            :selectedTime="selectedTime"
            :isPdfGenerating="isPdfGenerating"
            @update:selectedTime="updateSelectedTime"
            @viewTabChanged="handleViewTabChange"
            @reportRequested="handleReportRequest"
            @chartDownloadRequested="handleChartDownload"
            @chartDataUpdated="handleChartDataUpdate"
        ></TabContent>

        <!-- EL-802: program huni / adım süresi / yanlış cevap / trivia raporları -->
        <ProgramAnalyticsPanel v-if="mainTabIndex === 4" />
      </div>

      <div v-if="showCustomReport" class="past-reports-container">
        <h2 class="section-title">{{ getTabBasedTitle }}</h2>

        <div class="filters-row">
          <CreationSelectBox
              v-model="selectedPeriod"
              :maxTextLength="15"
              :label="this.$t('time_interval')"
              :tabType="getReportTypeForSelectBox()"
              @update:modelValue="handlePeriodChange"
              @filterChange="handlePeriodFilterChange"
              @optionsLoaded="handlePeriodOptionsLoaded"
          />
        </div>

        <div v-if="pastReportsLoading || tabLoading[mainTabIndex]" class="loading-wrapper"></div>

        <div v-else-if="isCurrentTabEmpty" class="empty-state-wrapper">
          <EmptyState
              :title="currentTabEmptyStateProps.title"
              :description="currentTabEmptyStateProps.description"
          />
        </div>

        <div v-else-if="currentTabReports.length > 0" class="reports-cards-grid">
          <template v-for="report in currentTabReports" :key="report.id">
            <div
                :data-report-id="report.id"
                class="report-card"
            >
              <div class="status-tag">
                <div class="status-dot"></div>
                <span>{{$t('reports.created_title')}}</span>
              </div>

              <span class="report-date">{{ formatDate(report.createdAt) }}</span>
              <h3 class="report-title">{{ report.title }}</h3>

              <div class="report-type-tag">
                {{ report.typeName }}
              </div>

              <div class="card-actions">
                <button class="action-button" @click="downloadReport(report)" :disabled="isPdfGenerating">
                  <VueSpinnerIos v-if="isPdfGenerating" size="16" color="#282A36" />
                  <svg v-else width="14" height="12" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7 0.498535C8.04584 0.498535 9.05665 0.879917 9.8418 1.5708C10.6267 2.26166 11.1326 3.21526 11.2656 4.25244L11.2959 4.48779L11.498 4.61279C12.1065 4.99134 12.58 5.55276 12.8506 6.21631C13.1211 6.87981 13.1752 7.61211 13.0049 8.30811C12.8346 9.00406 12.4486 9.62862 11.9023 10.0923C11.3599 10.5526 10.6864 10.8305 9.97754 10.8882L4.02148 10.8989C2.27315 10.7461 0.900391 9.28207 0.900391 7.49951L0.907227 7.28369C0.938954 6.78093 1.08176 6.29016 1.32715 5.84814C1.60756 5.34313 2.01248 4.91798 2.50293 4.61279L2.7041 4.48779L2.73438 4.25244C2.8674 3.21522 3.37418 2.26167 4.15918 1.5708C4.89523 0.923118 5.82914 0.547837 6.80469 0.503418L7 0.498535ZM7 0.700684C6.00362 0.700706 5.04135 1.06341 4.29297 1.72119C3.59127 2.33804 3.12199 3.17476 2.96094 4.09131L2.93359 4.27588V4.27686L2.89062 4.60596L2.61035 4.78174C2.03741 5.13772 1.59191 5.66606 1.33691 6.29053C1.08194 6.91499 1.03042 7.60402 1.19043 8.25928C1.35045 8.91448 1.71348 9.50233 2.22754 9.93896C2.67741 10.3211 3.22284 10.5706 3.80273 10.6616L4.05273 10.6909H4.05371L4.1582 10.6987L4.17676 10.6997H9.82422L9.84277 10.6987L9.94629 10.6909H9.94727C10.6199 10.6392 11.2592 10.3756 11.7734 9.93896C12.2875 9.50237 12.6505 8.91449 12.8105 8.25928C12.9706 7.60405 12.92 6.915 12.665 6.29053C12.4101 5.66599 11.9636 5.13776 11.3906 4.78174H11.3896L11.1084 4.60596L11.0674 4.27686L11.0664 4.27588L11.0391 4.09131C10.878 3.17478 10.4087 2.33803 9.70703 1.72119C8.95864 1.06344 7.99636 0.700684 7 0.700684ZM7.10059 4.1001V6.49951H8.36035L7 8.19971L5.64062 6.49951H6.90039V4.1001H7.10059Z" fill="#282A36" stroke="#282A36"/>
                  </svg>
                </button>
              </div>
            </div>
          </template>
        </div>

        <div v-else class="empty-state-wrapper">
          <EmptyState
              title="Henüz oluşturulmuş rapor yok"
              description="İlk raporunuzu oluşturmak için filtreleri kullanın"
          />
        </div>

        <div class="pagination-align-center">
          <Pagination
              :current-page="currentPage"
              :total-items="totalReports"
              :per-page="20"
              :on-page-change="handlePage"
          />
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import { defineComponent } from 'vue';
import axios from 'axios';
import ApexCharts from 'apexcharts';
import Layout from "@/components/layouts/Layout.vue";
import ProgramAnalyticsPanel from "@/components/reports/ProgramAnalyticsPanel.vue";
import SelectBoxLabelled from "@/components/SelectBoxLabelled.vue";
import ProgramCard from "@/components/ProgramCard.vue";
import UserTable from "@/components/tables/User/UserTable.vue";
import ConfirmDeleteModal from "@/components/modals/ConfirmDeleteModal.vue";
import InviteUserModal from "@/components/modals/InviteUserModal.vue";
import ExpandableSearch from "@/components/ExpandableSearch.vue";
import DepartmentModal from "@/components/modals/DepartmentModal.vue";
import DepartmentsTable from "@/components/tables/Departments/DepartmentsTable.vue";
import TransactionTable from "@/components/tables/TransactionTable.vue";
import TestFeaturesTable from "@/components/tables/Test/TestFeaturesTable.vue";
import EmptyState from "@/components/EmptyState.vue";
import TabContent from "@/components/TabContent.vue";
import TimeSelectBox from "@/components/TimeSelectBox.vue";
import CreationSelectBox from "@/components/CreationSelectBox.vue";
import reportsService from '@/service/reportsService';
import ReportsFilters from "@/components/tables/Report/ReportsFilters.vue";
import htmlToPdfService from '@/service/htmlToPdfService';
import {VueSpinnerIos} from 'vue3-spinners';
import UserFilters from "@/components/tables/User/UserFilters.vue";
import Pagination from "@/components/Pagination.vue";

export default defineComponent({
  name: 'Reports',

  components: {
    UserFilters,
    CreationSelectBox,
    TabContent,
    TestFeaturesTable,
    TransactionTable,
    DepartmentsTable,
    DepartmentModal,
    ExpandableSearch,
    InviteUserModal,
    ConfirmDeleteModal,
    UserTable,
    ProgramCard,
    SelectBoxLabelled,
    TimeSelectbox: TimeSelectBox,
    Layout,
    EmptyState,
    ReportsFilters,
    VueSpinnerIos,
    Pagination,
    ProgramAnalyticsPanel,
  },

  data() {
    return {
      mainTabIndex: 0,
      viewTabIndex: 0,
      isLoading: false,
      pastReportsLoading: false,
      selectedTest: '3',
      selectedTime: 'week',
      emptyStateVisible: false,
      showCustomReport: false,
      hasCreatedReports: false,
      selectedPeriod: 'all',
      userReports: [],
      currentPage: 1,
      totalReports: 0,
      totalPages: 1,
      activeFilters: [],
      periodParams: null,
      showCreateReportModal: false,
      isCreatingReport: false,
      apiConnected: false,
      lastApiCallTime: null,
      apiErrorCount: 0,
      apiResponse: null,
      isLoadingChartData: false,
      loadDataTimeout: null,
      isPdfGenerating: false,
      lastLoadTime: null,

      tabReports: {
        0: [],
        1: [],
        2: [],
        3: []
      },

      tabLoading: {
        0: false,
        1: false,
        2: false,
        3: false
      },

      chartData: {
        userChart: {
          series: [],
          categories: []
        },
        genderChart: {
          labels: [],
          series: []
        },
        rolesChart: {
          categories: [],
          data: []
        },
        departmentsChart: {
          labels: [],
          series: []
        }
      },

      charts: {
        userLineChart: null,
        genderPieChart: null,
        userRolesChart: null,
        departmentDonutChart: null
      }
    };
  },

  computed: {
    getTabBasedTitle() {
      const tabTitles = {
        0: this.$t('testHistoryReports'),
        1: this.$t('programHistoryReports'),
        2: this.$t('userHistoryReports'),
        3: this.$t('visualTestHistoryReports'),
        4: this.$t('blogHistoryReports'),
        5: this.$t('expertHistoryReports')
      };
      return tabTitles[this.mainTabIndex] || this.$t('historyReports');
    },

    currentTabReports() {
      return this.tabReports[this.mainTabIndex] || [];
    },

    isCurrentTabEmpty() {
      return !this.tabLoading[this.mainTabIndex] &&
          this.currentTabReports.length === 0;
    },

    currentTabEmptyStateProps() {
      const emptyStateConfig = {
        0: {
          title: this.$t('reports.no_test_reports') || "Henüz test raporu yok",
        },
        1: {
          title: this.$t('reports.no_program_reports') || "Henüz program raporu yok",
        },
        2: {
          title: this.$t('reports.no_user_reports') || "Henüz kullanıcı raporu yok",
        },
        3: {
          title: this.$t('reports.no_gamelab_reports') || "Henüz görsel test raporu yok",
        }
      };

      return emptyStateConfig[this.mainTabIndex] || {
        title: 'Henüz rapor yok',
      };
    },

    formattedDate() {
      try {
        const today = new Date();
        const day = today.getDate();
        const month = today.toLocaleString('en-US', {month: 'long'});
        const year = today.getFullYear();
        return `${day} ${month} ${year}`;
      } catch (error) {
        return new Date().toLocaleDateString();
      }
    }
  },

  methods: {
    getColorByUuid(uuid) {
      if (!uuid) return '#3da33e';

      const colors = ['#3da33e', '#1c3dd5', '#f09460', '#859ddd', '#4b68f8'];
      const hash = uuid.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
      return colors[Math.abs(hash) % colors.length];
    },

    extractColorsFromSeries(series) {
      if (!series || !Array.isArray(series)) return ['#3da33e'];

      return series.map(item => {
        if (item.uuid) {
          return this.getColorByUuid(item.uuid);
        }
        if (item.color) {
          return item.color;
        }
        return '#3da33e';
      });
    },

    extractColorsFromGenderData(genderData) {
      if (!genderData || !Array.isArray(genderData)) return ['#f09460','#4a68f8','#000000'];

      return genderData.map((item, index) => {
        if (item.uuid) {
          return this.getColorByUuid(item.uuid);
        }
        if (item.color) {
          return item.color;
        }
        const defaultColors = ['#f09460','#4a68f8','#000000'];
        return defaultColors[index % defaultColors.length];
      });
    },

    extractColorsFromRolesData(rolesData) {
      if (!rolesData || !Array.isArray(rolesData)) return ['#3da33e', '#1c3dd5', '#f09460', '#859ddd', '#4b68f8'];

      return rolesData.map((item, index) => {
        if (item.uuid) {
          return this.getColorByUuid(item.uuid);
        }
        if (item.color) {
          return item.color;
        }
        const defaultColors = ['#3da33e', '#1c3dd5', '#f09460', '#859ddd', '#4b68f8'];
        return defaultColors[index % defaultColors.length];
      });
    },

    extractColorsFromDepartmentsData(departmentsData) {
      if (!departmentsData || !Array.isArray(departmentsData)) return ['#1c3dd5', '#eba93f', '#6e3ff3', '#f09460', '#3da33e'];

      return departmentsData.map((item, index) => {
        if (item.uuid) {
          return this.getColorByUuid(item.uuid);
        }
        if (item.color) {
          return item.color;
        }
        const defaultColors = ['#1c3dd5', '#eba93f', '#6e3ff3', '#f09460', '#3da33e'];
        return defaultColors[index % defaultColors.length];
      });
    },

    hasChartData(chartType) {
      switch (chartType) {
        case 'userChart':
          return this.chartData.userChart?.series?.length > 0 &&
              this.chartData.userChart?.categories?.length > 0;
        case 'genderChart':
          return this.chartData.genderChart?.series?.length > 0 &&
              this.chartData.genderChart?.labels?.length > 0;
        case 'rolesChart':
          return this.chartData.rolesChart?.data?.length > 0 &&
              this.chartData.rolesChart?.categories?.length > 0;
        case 'departmentsChart':
          return this.chartData.departmentsChart?.series?.length > 0 &&
              this.chartData.departmentsChart?.labels?.length > 0;
        default:
          return false;
      }
    },

    showNoDataMessage(chartElement, height = 300) {
      if (!chartElement) return;

      chartElement.innerHTML = `
    <div style="
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: ${height}px;
      color: #9CA3AF;
      font-family: Inter, sans-serif;
    ">
      <div style="
        font-size: 48px;
        opacity: 0.3;
      "></div>
      <div style="
        font-size: 16px;
        font-family: 'Euclid Black', sans-serif;
      ">${this.$t('no_data_found')}</div>
      <div style="
        font-size: 14px;
        opacity: 0.7;
      ">${this.$t('reports.no_data_description')}</div>
    </div>
  `;
    },

    updateSelectedTime(time) {
      if (!time) return;

      this.selectedTime = time;
    },

    handleViewTabChange(index) {
      if (typeof index !== 'number') return;

      if (this.viewTabIndex !== index) {
        this.viewTabIndex = index;
      }
    },

    handleReportRequest(reportInfo) {
      if (!reportInfo) return;

      if (reportInfo.viewTab === 1 && reportInfo.tableData) {
        this.exportTableToPDF(reportInfo);
      } else {
        this.generateCurrentChartsPDF();
      }
    },

    async exportTableToPDF(reportInfo) {
      const data = reportInfo.tableData;

      if (!data?.length) {
        this.$snackbar.add({
          text: this.$t('no_data_found'),
          type: "warning",
          duration: 3000
        });
        return;
      }

      try {
        this.isPdfGenerating = true;

        const { jsPDF } = await import('jspdf');

        const doc = new jsPDF({
          orientation: 'landscape',
          unit: 'mm',
          format: 'a4'
        });

        doc.setFontSize(16);
        const reportTypeTranslated = this.$t(reportInfo.type) || reportInfo.type.toUpperCase();
        doc.text(this.normalizeTurkishText(`${reportTypeTranslated} ${this.$t('report') || 'Raporu'}`), 14, 15);

        doc.setFontSize(10);
        const locale = this.$i18n?.locale === 'en' ? 'en-US' : 'tr-TR';
        doc.text(this.normalizeTurkishText(`${this.$t('date') || 'Tarih'}: ${new Date().toLocaleDateString(locale)}`), 14, 22);

        const columns = this.getColumns(reportInfo.type);
        let y = 32;

        const colWidths = reportInfo.type === 'user'
            ? [60, 25, 45, 35, 30, 35]
            : [80, 40, 30, 40, 40];

        const colX = reportInfo.type === 'user'
            ? [14, 74, 99, 144, 179, 209]
            : [14, 94, 134, 164, 204];

        doc.setFillColor(31, 41, 55);
        doc.rect(14, y - 5, 270, 8, 'F');

        doc.setFontSize(10);
        doc.setTextColor(255, 255, 255);
        columns.forEach((col, i) => {
          doc.text(this.normalizeTurkishText(col), colX[i] + 2, y);
        });

        y += 3;
        doc.setDrawColor(31, 41, 55);
        doc.line(14, y, 284, y);

        doc.setTextColor(0, 0, 0);
        doc.setFontSize(9);

        data.slice(0, 40).forEach((item, index) => {
          y += 7;

          if (y > 190) {
            doc.addPage();
            y = 20;

            doc.setFillColor(31, 41, 55);
            doc.rect(14, y - 5, 270, 8, 'F');
            doc.setTextColor(255, 255, 255);
            columns.forEach((col, i) => {
              doc.text(this.normalizeTurkishText(col), colX[i] + 2, y);
            });
            y += 10;
            doc.setTextColor(0, 0, 0);
          }

          if (index % 2 === 1) {
            doc.setFillColor(249, 250, 251);
            doc.rect(14, y - 5, 270, 6, 'F');
          }

          const row = this.getRowData(item, reportInfo.type);
          row.forEach((cell, i) => {
            let cellText = String(cell || '-');

            let displayText = cellText;
            if (i === 0 && cellText.length > 40) {
              displayText = cellText.substring(0, 40) + '...';
            } else if (cellText.length > 20 && i !== 0) {
              displayText = cellText.substring(0, 20);
            }

            doc.text(this.normalizeTurkishText(displayText), colX[i] + 2, y);
          });
        });

        const pageCount = doc.internal.getNumberOfPages();
        const pageText = this.$t('page') || 'Sayfa';
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setFontSize(8);
          doc.setTextColor(128, 128, 128);
          doc.text(
              this.normalizeTurkishText(`${pageText} ${i} / ${pageCount}`),
              doc.internal.pageSize.getWidth() / 2,
              doc.internal.pageSize.getHeight() - 10,
              { align: 'center' }
          );
        }

        doc.save(`${reportInfo.type}_${new Date().toISOString().slice(0,10)}.pdf`);

        this.$snackbar.add({
          text: this.$t('pdf_downloaded'),
          type: "success",
          duration: 3000
        });
      } catch (error) {
        this.$snackbar.add({
          text: this.$t('download_error', { error: error.message }),
          type: "error",
          duration: 3000
        });
      } finally {
        this.isPdfGenerating = false;
      }
    },

    getColumns(type) {
      const cols = {
        test: [
          this.$t('test'),
          this.$t('status'),
          this.$t('question'),
          this.$t('completion'),
          this.$t('date')
        ],
        program: [
          this.$t('program'),
          this.$t('status'),
          this.$t('module_count'),
          this.$t('completion'),
          this.$t('date')
        ],
        user: [
          this.$t('email'),
          this.$t('status'),
          this.$t('role'),
          this.$t('department'),
          this.$t('created_at'),
          this.$t('last_login')
        ],
        gameLabs: [
          this.$t('gameLabs_title'),
          this.$t('status'),
          this.$t('question'),
          this.$t('completion'),
          this.$t('date')
        ]
      };
      return cols[type] || cols.test;
    },

    getRowData(item, type) {
      const rows = {
        test: [
          item.name || item.title || '-',
          item.isPublish ? this.$t('active') : this.$t('draft'),
          item.questionsCount || 0,
          (item.progress !== undefined ? item.progress :
              item.completionRate !== undefined ? item.completionRate : 0) + '%',
          this.formatDate(item.createdAt || item.created_at) || '-'
        ],
        program: [
          item.name || item.title || '-',
          item.isPublish ? this.$t('active') : this.$t('draft'),
          item.modulesCount || item.moduleCount || 0,
          (item.progress !== undefined ? item.progress :
              item.completionRate !== undefined ? item.completionRate : 0) + '%',
          this.formatDate(item.createdAt || item.created_at) || '-'
        ],
        user: [
          item.email || '-',
          item.status ? this.$t('active') : this.$t('inactive'),
          item.role?.name || item.role?.key || '-',
          item.departments || item.department || this.$t('no_department'),
          this.formatDate(item.createdAt || item.created_at) || '-',
          item.lastLogin ? this.formatDate(item.lastLogin) : this.$t('no_login')
        ],
        gameLabs: [
          item.name || item.title || '-',
          item.isPublish ? this.$t('active') : this.$t('draft'),
          item.questionsCount || 0,
          (item.progress !== undefined ? item.progress :
              item.completionRate !== undefined ? item.completionRate : 0) + '%',
          this.formatDate(item.createdAt || item.created_at) || '-'
        ]
      };
      return rows[type] || rows.test;
    },

    changeMainTab(index) {
      if (this.mainTabIndex === index || typeof index !== 'number') return;

      this.mainTabIndex = index;
      this.viewTabIndex = 0;

      // Program Analitiği sekmesinin geçmiş rapor (PDF) akışı yok.
      // TabContent kaldırıldığı için chartDataUpdated gelmez, grafikleri burada temizliyoruz.
      if (index === 4) {
        this.showCustomReport = false;
        this.destroyCharts();
        return;
      }

      if (this.showCustomReport) {
        this.resetFilters();

        if (!this.tabReports[this.mainTabIndex] || this.tabReports[this.mainTabIndex].length === 0) {
          this.getReports();
        } else {
          this.userReports = this.tabReports[this.mainTabIndex];
        }
      }
    },

    handleChartDataUpdate(newChartData) {

      if (!newChartData) {
        return;
      }

      this.destroyCharts();

      const success = this.processApiResponse(newChartData, this.getCurrentTabType());

      if (success) {

        this.$nextTick(() => {
          this.initializeAllCharts();
        });
      } else {
        this.resetChartData();

        this.$nextTick(() => {
          this.initializeAllCharts();
        });
      }
    },

    getCurrentTabType() {
      const tabTypeMapping = {
        0: 'test',
        1: 'program',
        2: 'users',
        3: 'gameLabs'
      };
      return tabTypeMapping[this.mainTabIndex] || 'users';
    },

    toggleCustomReport() {
      this.showCustomReport = !this.showCustomReport;
      if (this.showCustomReport) {
        this.resetFilters();
        this.getReports();
      }
    },

    resetFilters() {
      this.currentPage = 1;
      this.activeFilters = [];
      this.selectedPeriod = 'all';
      this.totalReports = 0;
    },

    testClick() {
      alert('Buton çalışıyor!');
      this.openCreateReportModal();
    },

    openCreateReportModal() {
      this.showCreateReportModal = true;
    },

    closeCreateReportModal(){
      this.showCreateReportModal = false;
    },

    async handleCreateReport(reportData) {
      if (!reportData || !reportData.name || !reportData.type) return;

      this.isCreatingReport = true;
      try {
        await this.createNewReport(reportData.name, reportData.type);
        this.closeCreateReportModal();
      } catch (error) {
      } finally {
        this.isCreatingReport = false;
      }
    },

    getCurrentReportType() {
      const tabTypeMapping = {
        0: 'selfAssessments',
        1: 'programs',
        2: 'users',
        3: 'gameLabs'
      };
      return tabTypeMapping[this.mainTabIndex] || 'selfAssessments';
    },

    highlightNewReport(reportId) {
      if (!reportId) return;

      this.$nextTick(() => {
        const reportCard = document.querySelector(`[data-report-id="${reportId}"]`);
        if (reportCard) {
          reportCard.classList.add('new-report-highlight');
          setTimeout(() => {
            if (reportCard) {
              reportCard.classList.remove('new-report-highlight');
            }
          }, 3000);
        }
      });
    },

    loadReportData() {
      if (this.isLoadingChartData) {
        return;
      }

      if (this.lastLoadTime && (Date.now() - this.lastLoadTime < 500)) {
        return;
      }

      this.lastLoadTime = Date.now();

      this.isLoadingChartData = true;
      this.destroyCharts();

      const tabTypeMapping = {
        0: 'test',
        1: 'program',
        2: 'users',
        3: 'gameLabs'
      };

      const currentType = tabTypeMapping[this.mainTabIndex] || 'users';
      let currentPeriod = this.selectedTime || 'week';

      const urlParams = new URLSearchParams(window.location.search);
      const addedDate = urlParams.get('created_at[start]');
      const addedDateEnd = urlParams.get('created_at[end]');

      if (addedDate && addedDateEnd) {
        this.selectedTime = 'custom';
        currentPeriod = 'custom';

        this.tryApiCall(currentType, currentPeriod, {
          startDate: addedDate,
          endDate: addedDateEnd
        });
        return;
      }

      this.tryApiCall(currentType, currentPeriod);
    },

    tryApiCall(reportType, period, dateRange = null) {
      if (!reportsService || typeof reportsService.getReportData !== 'function') {
        this.updateApiStatus(false);
        this.isLoadingChartData = false;
        this.resetChartData();
        return;
      }

      const apiTypeMapping = {
        'test': 'selfAssessments',
        'program': 'programs',
        'users': 'users',
        'gameLabs': 'gameLabs'
      };

      const apiType = apiTypeMapping[reportType];

      const reportOptions = {
        type: apiType,
        parameterType: this.viewTabIndex === 1 ? 'properties' : 'users',
        period: period,
        status: 1,
        page: 1,
        limit: 20
      };

      if (dateRange) {
        reportOptions['created_at[start]'] = dateRange.startDate;
        reportOptions['created_at[end]'] = dateRange.endDate;
      }

      if (!reportsService.getReportData) {
        this.isLoadingChartData = false;
        this.resetChartData();
        return;
      }

      reportsService.getReportData(reportOptions)
          .then(response => {
            if (response?.data?.data) {
              const success = this.processApiResponse(response.data, reportType);

              if (success) {
                this.updateApiStatus(true);

                this.$nextTick(() => {
                  this.initializeAllCharts();
                  this.isLoadingChartData = false;
                });
              } else {
                this.updateApiStatus(false);
                this.isLoadingChartData = false;
                this.resetChartData();
              }
            } else {
              this.updateApiStatus(false);
              this.isLoadingChartData = false;
              this.resetChartData();
            }
          })
          .catch(err => {
            this.updateApiStatus(false, err);
            this.isLoadingChartData = false;
            this.resetChartData();
          });
    },

    resetChartData() {
      this.chartData = {
        userChart: { series: [], categories: [] },
        genderChart: { labels: [], series: [] },
        rolesChart: { categories: [], data: [] },
        departmentsChart: { labels: [], series: [] }
      };
    },

    processApiResponse(response, chartType) {

      if (!response?.data) {
        return false;
      }

      const data = response.data;

      const dataKeyMapping = {
        'test': 'selfAssessments',
        'program': 'programs',
        'users': 'users',
        'gameLabs': 'gameLabs'
      };

      const dataKey = dataKeyMapping[chartType] || 'selfAssessments';

      try {
        if (data[dataKey]) {
          const lineChartData = data[dataKey];

          this.chartData.userChart = {
            series: lineChartData.series || [],
            categories: lineChartData.categories || []
          };

        } else {
          this.chartData.userChart = { series: [], categories: [] };
        }

        if (data.gender?.data) {
          const genderData = data.gender.data;

          this.chartData.genderChart = {
            labels: genderData.map(item => item?.name || 'Unknown'),
            series: genderData.map(item => item?.count || 0),
            originalData: genderData
          };

        } else {
          this.chartData.genderChart = { labels: [], series: [], originalData: [] };
        }

        if (data.roles?.data && data.roles.data.length > 0) {
          const rolesData = data.roles.data;

          this.chartData.rolesChart = {
            categories: rolesData.map(item => item?.name || 'Unknown'),
            data: rolesData.map(item => item?.count || 0),
            originalData: rolesData
          };

        } else {
          this.chartData.rolesChart = { categories: [], data: [], originalData: [] };
        }

        if (data.departments?.data && data.departments.data.length > 0) {
          const departmentsData = data.departments.data;

          this.chartData.departmentsChart = {
            labels: departmentsData.map(item => item?.name || 'Unknown'),
            series: departmentsData.map(item => item?.count || 0),
            originalData: departmentsData
          };

        } else {
          this.chartData.departmentsChart = { labels: [], series: [], originalData: [] };
        }

        this.apiResponse = response;
        return true;

      } catch (error) {
        return false;
      }
    },

    updateApiStatus(connected, error = null) {
      this.apiConnected = connected;
      this.lastApiCallTime = new Date();

      if (error) {
        this.apiErrorCount++;
      } else if (connected) {
        this.apiErrorCount = 0;
      }
    },

    initializeAllCharts() {
      const checkElement = (id) => {
        if (!id || typeof id !== 'string') return false;
        const element = document.getElementById(id);
        return !!element;
      };

      if (!this.chartData) {
        return;
      }

      if (checkElement('userLineChart')) {
        try {
          this.initUserLineChart();
        } catch (error) {
        }
      }

      if (checkElement('genderPieChart')) {
        try {
          this.initGenderPieChart();
        } catch (error) {
        }
      }

      if (checkElement('userRolesChart')) {
        try {
          this.initUserRolesChart();
        } catch (error) {
        }
      }

      if (checkElement('departmentDonutChart')) {
        try {
          this.initDepartmentDonutChart();
        } catch (error) {
        }
      }
    },

    initializeCharts() {
      this.initializeAllCharts();
    },

    initUserLineChart() {
      const chartElement = document.getElementById('userLineChart');
      if (!chartElement) return;

      if (!this.hasChartData('userChart')) {
        this.showNoDataMessage(chartElement, 300);
        return;
      }

      const { series, categories } = this.chartData.userChart;

      try {
        let maxValue = 10;
        try {
          maxValue = Math.max(...series.flatMap(item =>
              Array.isArray(item?.data) ? item.data.map(point =>
                  typeof point === 'object' ? (point?.y || 0) : (point || 0)
              ) : []
          ));
          if (maxValue <= 0 || !isFinite(maxValue)) maxValue = 10;
        } catch (error) {
          maxValue = 10;
        }

        const dynamicColors = this.extractColorsFromSeries(series);

        const userLineChartOptions = {
          chart: {
            type: 'line',
            height: 300,
            fontFamily: 'Inter, sans-serif',
            toolbar: {
              show: true,
              offsetY: -50,
              tools:{
                download:true
              }

            },
            background: 'transparent',
            zoom: { enabled: false },
            padding: {
              top: 0,
              right: 0,
              bottom: 0,
              left: 0
            },
            margin: {
              top: 10,
              right: 10,
              bottom: 10,
              left: 10
            }
          },
          dataLabels: { enabled: false },
          stroke: {
            curve: 'smooth',
            width: 2
          },
          colors: dynamicColors,
          series: series,
          markers: {
            size: 0,
            strokeWidth: 0,
            hover: {
              size: 5,
              strokeWidth: 2,
              strokeColors: '#fff'
            }
          },
          xaxis: {
            categories: categories,
            labels: {
              style: {
                fontSize: '10px',
                colors: '#9CA3AF',
                fontWeight: 400,
                fontFamily: 'Euclid, sans-serif'
              },
              offsetY: 2
            },
            axisBorder: {
              show: false
            },
            axisTicks: {
              show: false
            },
            crosshairs: {
              show: true,
              width: 1,
              position: 'back',
              opacity: 0.2,
              stroke: {
                color: '#9CA3AF',
                width: 1,
                dashArray: 0
              }
            }
          },
          yaxis: {
            min: 0,
            max: maxValue + 5,
            labels: {
              style: {
                fontSize: '10px',
                colors: '#9CA3AF',
                fontWeight: 400,
                fontFamily: 'Inter, sans-serif'
              },
              formatter: function(val) {
                return Math.round(val || 0);
              }
            }
          },
          grid: {
            borderColor: '#F3F4F6',
            strokeDashArray: 0,
            xaxis: {
              lines: {
                show: true
              }
            },
            yaxis: {
              lines: {
                show: false
              }
            },
            padding: {
              top: 5,
              right: 10,
              bottom: 5,
              left: 5
            }
          },
          legend: {
            show: true,
            position: 'bottom',
            fontSize: '10px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            offsetY: 8,
            markers: {
              width: 6,
              height: 6,
              radius: 50,
              offsetX: -2
            },
            itemMargin: {
              horizontal: 10,
              vertical: 4
            },
            labels: {
              colors: '#6B7280'
            }
          },
          tooltip: {
            enabled: true,
            shared: true,
            intersect: false,
            theme: 'dark',
            style: {
              fontSize: '11px',
              fontFamily: 'Inter, sans-serif'
            },
            custom: function({series, seriesIndex, dataPointIndex, w}) {
              if (seriesIndex === undefined || !series || !w?.globals) return '';

              const value = series[seriesIndex]?.[dataPointIndex] || 0;
              const seriesName = w.globals.seriesNames?.[seriesIndex] || 'Unknown';

              return `
        <div style="
          padding: 6px 10px;
          background: rgba(17,24,39,0.95);
          border-radius: 6px;
          color: white;
          font-size: 11px;
          font-weight: 500;
          white-space: nowrap;
          border: none;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        ">
          <div>${seriesName}: ${value}</div>
        </div>
      `;
            }
          },
          responsive: [{
            breakpoint: 768,
            options: {
              chart: {
                height: 200
              },
              legend: {
                position: 'bottom'
              }
            }
          }]
        };

        if (this.charts.userLineChart) {
          this.charts.userLineChart.destroy();
        }
        this.charts.userLineChart = new ApexCharts(chartElement, userLineChartOptions);
        this.charts.userLineChart.render();
      } catch (error) {
        this.showNoDataMessage(chartElement, 300);
      }
    },

    initGenderPieChart() {
      const chartElement = document.getElementById('genderPieChart');
      if (!chartElement) return;

      if (!this.hasChartData('genderChart')) {
        this.showNoDataMessage(chartElement, 200);
        return;
      }

      const { labels, series, originalData } = this.chartData.genderChart;

      try {
        const dynamicColors = this.extractColorsFromGenderData(originalData || []);

        const genderPieChartOptions = {
          chart: {
            type: 'pie',
            height: 200,
            background: 'transparent',
            fontFamily: 'Inter, sans-serif',
            toolbar: {
              show: true,
              offsetY: -70,
              tools:{
                download:true
              }
            },
          },

          colors: ['#f09460','#4a68f8','#000000'],
          series: series,
          labels: labels,
          legend: {
            show: true,
            position: 'bottom',
            fontSize: '10px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            offsetY: 4,
            markers: {
              width: 6,
              height: 6,
              radius: 50,
              offsetX: -2
            },
            itemMargin: {
              horizontal: 8,
              vertical: 2
            },
            labels: {
              colors: '#6B7280'
            }
          },
          dataLabels: {
            enabled: true,
            style: {
              fontSize: '12px',
              fontWeight: 600,
              colors: ['#FFFFFF'],
              fontFamily: 'Inter, sans-serif'
            },
            formatter: function(val, opts) {
              const percentage = Math.round(val || 0);
              return `${percentage}%`;
            },
            dropShadow: {
              enabled: false
            }
          },
          plotOptions: {
            pie: {
              expandOnClick: false,
              donut: {
                size: '0%'
              },
              customScale: 1
            }
          },
          stroke: {
            show: true,
            width: 2,
            colors: ['#ffffff']
          },
          tooltip: {
            enabled: true,
            theme: 'dark',
            style: {
              fontSize: '11px',
              fontFamily: 'Inter, sans-serif'
            },
            y: {
              formatter: function(val, opts) {
                if (!opts?.globals?.seriesTotals) return `${val || 0}`;

                const total = opts.globals.seriesTotals.reduce((a, b) => (a || 0) + (b || 0), 0);
                const percentage = total > 0 ? ((val || 0) / total * 100).toFixed(1) : '0';
                return `${val || 0} (${percentage}%)`;
              }
            }
          },
          responsive: [{
            breakpoint: 768,
            options: {
              chart: {
                height: 200
              }
            }
          }]
        };

        if (this.charts.genderPieChart) {
          this.charts.genderPieChart.destroy();
        }
        this.charts.genderPieChart = new ApexCharts(chartElement, genderPieChartOptions);
        this.charts.genderPieChart.render();
      } catch (error) {
        this.showNoDataMessage(chartElement, 200);
      }
    },

    initUserRolesChart() {
      const chartElement = document.getElementById('userRolesChart');
      if (!chartElement) return;

      if (!this.hasChartData('rolesChart')) {
        this.showNoDataMessage(chartElement, 200);
        return;
      }

      const { categories, data, originalData } = this.chartData.rolesChart;

      try {
        const dynamicColors = this.extractColorsFromRolesData(originalData || []);

        const maxValue = Math.max(...data.filter(val => typeof val === 'number' && isFinite(val))) || 10;

        const userRolesChartOptions = {
          chart: {
            type: 'bar',
            height: 200,
            fontFamily: 'Inter, sans-serif',
            toolbar: {
              show: true,
              offsetY: -70,
              tools:{
                download:true
              }

            },
            background: 'transparent'
          },
          plotOptions: {
            bar: {
              horizontal: true,
              barHeight: '55%',
              distributed: true,
              borderRadius: 3,
              borderRadiusApplication: 'end',
              borderRadiusWhenStacked: 'last'
            }
          },
          colors: dynamicColors,
          series: [{
            name: 'Kullanıcı Sayısı',
            data: data.slice(0, 5).map(val => val || 0)
          }],
          xaxis: {
            categories: categories.slice(0, 5),
            min: 0,
            max: maxValue + 10,
            labels: {
              style: {
                fontSize: '10px',
                colors: '#9CA3AF',
                fontWeight: 400,
                fontFamily: 'Inter, sans-serif'
              }
            },
            axisBorder: {
              show: false
            },
            axisTicks: {
              show: false
            }
          },
          yaxis: {
            labels: {
              style: {
                fontSize: '10px',
                colors: '#6B7280',
                fontWeight: 400,
                fontFamily: 'Inter, sans-serif'
              },
              maxWidth: 100
            }
          },
          dataLabels: {
            enabled: true,
            style: {
              fontSize: '10px',
              fontWeight: 600,
              colors: ['transparent'],
              fontFamily: 'Inter, sans-serif'
            },
            formatter: function(val) {
              return Math.round(val || 0);
            },
            offsetX: 20
          },
          legend: {
            show: true,
            position: 'bottom',
            fontSize: '10px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            offsetY: 8,
            markers: {
              width: 6,
              height: 6,
              radius: 50,
              offsetX: -2
            },
            itemMargin: {
              horizontal: 10,
              vertical: 4
            },
            labels: {
              colors: '#6B7280'
            }
          },
          grid: {
            borderColor: '#F3F4F6',
            strokeDashArray: 0,
            xaxis: {
              lines: {
                show: true
              }
            },
            yaxis: {
              lines: {
                show: false
              }
            },
            padding: {
              top: 0,
              right: 25,
              bottom: 0,
              left: 0
            }
          },
          tooltip: {
            enabled: true,
            theme: 'dark',
            style: {
              fontSize: '11px',
              fontFamily: 'Inter, sans-serif'
            },
            y: {
              formatter: function(val) {
                return Math.round(val || 0) + ' kullanıcı';
              }
            }
          },
          responsive: [{
            breakpoint: 768,
            options: {
              chart: {
                height: 180
              },
              plotOptions: {
                bar: {
                  barHeight: '50%'
                }
              }
            }
          }]
        };

        if (this.charts.userRolesChart) {
          this.charts.userRolesChart.destroy();
        }
        this.charts.userRolesChart = new ApexCharts(chartElement, userRolesChartOptions);
        this.charts.userRolesChart.render();
      } catch (error) {
        this.showNoDataMessage(chartElement, 200);
      }
    },

    initDepartmentDonutChart() {
      const chartElement = document.getElementById('departmentDonutChart');
      if (!chartElement) return;

      if (!this.hasChartData('departmentsChart')) {
        this.showNoDataMessage(chartElement, 250);
        return;
      }

      const { labels, series, originalData } = this.chartData.departmentsChart;

      try {
        const dynamicColors = this.extractColorsFromDepartmentsData(originalData || []);

        const departmentDonutChartOptions = {
          chart: {
            type: 'donut',
            height: 250,
            background: 'transparent',
            fontFamily: 'Inter, sans-serif',
            toolbar: {
              show: true,
              offsetY: -70,
              tools:{
                download:true
              }

            },
          },
          colors: dynamicColors,
          series: series.slice(0, 5).map(val => val || 0),
          labels: labels.slice(0, 5),
          legend: {
            show: true,
            position: 'bottom',
            fontSize: '10px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            offsetY: 0,
            markers: {
              width: 6,
              height: 6,
              radius: 50,
              offsetX: -2
            },
            itemMargin: {
              horizontal: 6,
              vertical: 2
            },
            labels: {
              colors: '#6B7280'
            }
          },
          dataLabels: {
            enabled: false
          },
          plotOptions: {
            pie: {
              donut: {
                size: '60%',
                labels: {
                  show: true,
                  value: {
                    show: true,
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#000',
                    fontFamily: 'Inter, sans-serif',
                    offsetY: 12,
                    formatter: function (val) {
                      return val || '0';
                    }
                  }
                }
              },
              expandOnClick: false,
              customScale: 1.0
            }
          },
          stroke: {
            show: true,
            width: 2,
            colors: ['#ffffff']
          },
          tooltip: {
            enabled: true,
            theme: 'dark',
            style: {
              fontSize: '11px',
              fontFamily: 'Inter, sans-serif'
            },
            y: {
              formatter: function(val, opts) {
                if (!opts?.globals?.seriesTotals) return `${val || 0}`;

                const total = opts.globals.seriesTotals.reduce((a, b) => (a || 0) + (b || 0), 0);
                const percentage = total > 0 ? ((val || 0) / total * 100).toFixed(1) : '0';
                return `${val || 0} (${percentage}%)`;
              }
            }
          },
          responsive: [{
            breakpoint: 768,
            options: {
              chart: {
                height: 200
              },
              plotOptions: {
                pie: {
                  donut: {
                    size: '55%'
                  }
                }
              }
            }
          }]
        };

        if (this.charts.departmentDonutChart) {
          this.charts.departmentDonutChart.destroy();
        }
        this.charts.departmentDonutChart = new ApexCharts(chartElement, departmentDonutChartOptions);
        this.charts.departmentDonutChart.render();
      } catch (error) {
        this.showNoDataMessage(chartElement, 250);
      }
    },

    destroyCharts() {
      try {
        Object.keys(this.charts || {}).forEach(key => {
          if (this.charts[key]) {
            try {
              this.charts[key].destroy();
            } catch (error) {
            }
            this.charts[key] = null;
          }
        });
      } catch (error) {
      }
    },

    generateAndDownloadReport(reportInfo) {
      if (!reportInfo) return;

      this.generateCurrentChartsPDF();
    },

    async getReports() {
      this.pastReportsLoading = true;
      this.tabLoading[this.mainTabIndex] = true;

      const tabTypeMapping = {
        0: 'selfAssessments',
        1: 'programs',
        2: 'users',
        3: 'gameLabs'
      };

      const reportType = tabTypeMapping[this.mainTabIndex];

      try {
        const tempFilters = [];

        let startDate = null;
        let endDate = null;

        if (this.periodParams?.start && this.periodParams?.end) {
          startDate = this.periodParams.start;
          endDate = this.periodParams.end;
        }
        else if (this.$route?.query?.['created_at[start]'] && this.$route?.query?.['created_at[end]']) {
          startDate = this.$route.query['created_at[start]'];
          endDate = this.$route.query['created_at[end]'];

          this.periodParams = { start: startDate, end: endDate };
          this.selectedPeriod = 'custom';
        }
        else {
          this.periodParams = null;
          this.selectedPeriod = 'all';
        }

        if (startDate && endDate) {
          tempFilters.push(
              { key: 'created_at[start]', value: startDate },
              { key: 'created_at[end]', value: endDate }
          );
        }

        const response = await reportsService.getSavedReports({
          type: reportType,
          page: this.currentPage,
          limit: 20,
          period: this.selectedPeriod === 'all' ? null : this.selectedPeriod,
          filters: tempFilters
        });

        if (response?.data?.responseType === 'success') {
          if (Array.isArray(response.data.data.data)) {
            const reports = response.data.data.data.map(item => ({
              id: item?.reportId || `report-${Date.now()}`,
              title: item?.name || `Rapor #${(item?.reportId || '').slice(0, 8)}`,
              typeName: this.getTypeDisplayName(item?.type),
              createdAt: item?.createdAt || new Date().toISOString(),
              status: 'Oluşturuldu',
              type: item?.type || 'unknown'
            }));

            this.tabReports[this.mainTabIndex] = reports;
            this.userReports = reports;
            this.totalReports = response.data.data.total;

          } else {
            this.tabReports[this.mainTabIndex] = [];
            this.userReports = [];
          }
        } else {
          this.tabReports[this.mainTabIndex] = [];
          this.userReports = [];
        }

      } catch (error) {
        this.tabReports[this.mainTabIndex] = [];
        this.userReports = [];
        this.totalReports = 0;
      } finally {
        this.pastReportsLoading = false;
        this.tabLoading[this.mainTabIndex] = false;
      }
    },

    handlePage(page) {
      if (this.pastReportsLoading) return;

      this.currentPage = page;
      this.getReports();
    },

    getTypeDisplayName(type) {
      const typeNames = {
        'selfAssessments': this.$t('testReport'),
        'programs': this.$t('programReport'),
        'users': this.$t('userReport'),
        'gameLabs': this.$t('gameLabReport')
      };
      return typeNames[type] || this.$t('unknownReport');
    },

    async generateCurrentChartsPDF() {
      try {
        this.isPdfGenerating = true;

        const chartDataForPDF = {
          charts: {},
          metadata: {
            tabType: this.mainTabIndex,
            selectedTime: this.selectedTime || 'week',
            date: new Date().toLocaleDateString('tr-TR')
          },
          chartLegends: {}
        };

        if (this.charts.userLineChart) {
          chartDataForPDF.charts.userChart = this.charts.userLineChart;
          chartDataForPDF.chartLegends.userChart = {
            series: this.chartData.userChart.series || [],
            categories: this.chartData.userChart.categories || []
          };
        }

        if (this.charts.genderPieChart) {
          chartDataForPDF.charts.genderChart = this.charts.genderPieChart;
          chartDataForPDF.chartLegends.genderChart = {
            labels: this.chartData.genderChart.labels || [],
            series: this.chartData.genderChart.series || [],
            originalData: this.chartData.genderChart.originalData || []
          };
        }

        if (this.charts.userRolesChart) {
          chartDataForPDF.charts.rolesChart = this.charts.userRolesChart;
          chartDataForPDF.chartLegends.rolesChart = {
            categories: this.chartData.rolesChart.categories || [],
            data: this.chartData.rolesChart.data || [],
            originalData: this.chartData.rolesChart.originalData || []
          };
        }

        if (this.charts.departmentDonutChart) {
          chartDataForPDF.charts.departmentsChart = this.charts.departmentDonutChart;
          chartDataForPDF.chartLegends.departmentsChart = {
            labels: this.chartData.departmentsChart.labels || [],
            series: this.chartData.departmentsChart.series || [],
            originalData: this.chartData.departmentsChart.originalData || []
          };
        }

        if (Object.keys(chartDataForPDF.charts).length === 0) {
          await this.initializeAllCharts();
          await new Promise(resolve => setTimeout(resolve, 1000));

          if (this.charts.userLineChart) {
            chartDataForPDF.charts.userChart = this.charts.userLineChart;
            chartDataForPDF.chartLegends.userChart = {
              series: this.chartData.userChart.series || [],
              categories: this.chartData.userChart.categories || []
            };
          }
          if (this.charts.genderPieChart) {
            chartDataForPDF.charts.genderChart = this.charts.genderPieChart;
            chartDataForPDF.chartLegends.genderChart = {
              labels: this.chartData.genderChart.labels || [],
              series: this.chartData.genderChart.series || [],
              originalData: this.chartData.genderChart.originalData || []
            };
          }
          if (this.charts.userRolesChart) {
            chartDataForPDF.charts.rolesChart = this.charts.userRolesChart;
            chartDataForPDF.chartLegends.rolesChart = {
              categories: this.chartData.rolesChart.categories || [],
              data: this.chartData.rolesChart.data || [],
              originalData: this.chartData.rolesChart.originalData || []
            };
          }
          if (this.charts.departmentDonutChart) {
            chartDataForPDF.charts.departmentsChart = this.charts.departmentDonutChart;
            chartDataForPDF.chartLegends.departmentsChart = {
              labels: this.chartData.departmentsChart.labels || [],
              series: this.chartData.departmentsChart.series || [],
              originalData: this.chartData.departmentsChart.originalData || []
            };
          }
        }

        if (Object.keys(chartDataForPDF.charts).length === 0) {
          if (Object.keys(chartDataForPDF.charts).length === 0) {
            this.$snackbar.add({
              text: this.$t('no_charts_warning'),
              type: 'warning',
              duration: 3000
            });
            return;
          }
          return;
        }

        const dateFilters = {
          startDate: this.filters?.find(f => f.key === 'created_at[start]')?.value,
          endDate: this.filters?.find(f => f.key === 'created_at[end]')?.value
        };

        const success = await htmlToPdfService.generatePDF(
            chartDataForPDF.charts,
            this.mainTabIndex,
            dateFilters,
            this.selectedTime || 'week',
            chartDataForPDF.chartLegends
        );

        if (success) {
          this.$snackbar.add({
            text: this.$t('pdf_generation_success'),
            type: 'success',
            duration: 3000
          });
        }

      } catch (error) {
        this.$snackbar.add({
          text: this.$t('pdf_generation_error', {
            error: error.message || this.$t('unknown_error')
          }),
          type: 'error',
          duration: 3000
        });
      } finally {
        this.isPdfGenerating = false;
      }
    },

    addFilter(filter) {
      if (!filter) return;

      this.activeFilters.push(filter);
      this.getReports();
    },

    removeFilter(index) {
      if (typeof index !== 'number' || index < 0 || index >= this.activeFilters.length) return;

      this.activeFilters.splice(index, 1);
      this.getReports();
    },

    formatDate(dateStr) {
      try {
        if (!dateStr) return "-";

        if (typeof dateStr === 'string' && dateStr.includes('.') && dateStr.includes(' ')) {
          const [datePart] = dateStr.split(' ');
          const [day, month, year] = datePart.split('.');

          if (!day || !month || !year) {
            return "-";
          }

          return `${day}/${month}/${year}`;

        } else {
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) return "-";

          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          return `${day}/${month}/${year}`;
        }
      } catch (e) {
        return "-";
      }
    },

    getReportTypeForSelectBox() {
      const mapping = {
        0: 'test', 1: 'program', 2: 'user', 3: 'gameLabs'
      };
      return mapping[this.mainTabIndex] || 'test';
    },

    handlePeriodChange(selectedValue) {
      this.selectedPeriod = selectedValue || 'all';
    },

    handlePeriodFilterChange(filterData) {
      if (filterData?.selectedRange === 'all') {
        this.periodParams = null;
        this.selectedPeriod = 'all';
      } else {
        this.periodParams = filterData?.creation || null;
        this.selectedPeriod = filterData?.selectedRange || 'all';
      }

      if (this.showCustomReport) {
        this.getReports();
      } else {
      }
    },

    handlePeriodOptionsLoaded(options) {
    },

    handleFilterSelected(filterName) {
      if (!filterName) return;

      if (filterName === 'date_range') {
        const reportName = `${this.getTypeDisplayName(this.getCurrentReportType())} - ${new Date().toLocaleDateString('tr-TR')}`;

      }

      this.closeCreateReportModal();
    },

    downloadReport(report) {
      if (!report) return;

      this.generateCurrentChartsPDF();
    },

    handleChartDownload(chartId) {

      if (this.charts && this.charts[chartId]) {
        try {
          this.charts[chartId].downloadSVG();
        } catch (error) {
          try {
            ApexCharts.exec(chartId, 'downloadSVG');
          } catch (execError) {
          }
        }
      } else {
        try {
          ApexCharts.exec(chartId, 'downloadSVG');
        } catch (error) {
        }
      }
    },
    normalizeTurkishText(text) {
      if (!text || typeof text !== 'string') return text;

      return text
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/ğ/g, 'g')
          .replace(/Ğ/g, 'G')
          .replace(/ı/g, 'i')
          .replace(/İ/g, 'I')
          .replace(/ö/g, 'o')
          .replace(/Ö/g, 'O')
          .replace(/ü/g, 'u')
          .replace(/Ü/g, 'U')
          .replace(/ş/g, 's')
          .replace(/Ş/g, 'S')
          .replace(/ç/g, 'c')
          .replace(/Ç/g, 'C');
    }
    },

  watch: {
    $route() {
      if (this.showCustomReport) {
        this.getReports();
      }
    },
    selectedPeriod() {
      if (this.showCustomReport) {
        this.getReports();
      }
    },

    mainTabIndex() {
      this.viewTabIndex = 0;
    }
  },

  beforeUnmount() {
    try {
      if (this.loadDataTimeout) {
        clearTimeout(this.loadDataTimeout);
      }
      this.destroyCharts();
    } catch (error) {
    }
  }
});
</script>

<style scoped>
.page-header {
  padding: 40px;
}
.reports-wrapper {
  height: 100vh;
}

.tab-panel {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-direction: row;
}

.main-content {
  padding: 0px;
  width: 100%;
}

.tab-panel-row {
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid #F2F4F7;
  border-top: 1px solid #F2F4F7;
  padding: 10px 40px 10px 20px;
  margin-bottom: 20px;
  flex-direction: row;
  width: auto;
  box-sizing: border-box;
}

.custom-report {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  font-family: 'Euclid Circular A', sans-serif;
  background: #F2F4F7;
  border-radius: 9px;
}

.tab-button.active, .tab-button:hover {
  background-color: #F2F4F7;
}

.tab-button {
  padding: 6px 10px;
  border-radius: 10px;
  border: none;
  outline: none;
  background-color: transparent;
  cursor: pointer;
}

.tab-button span {
  font-family: 'euclid_medium', sans-serif;
  font-size: 12px;
  color: #344054;
}

.past-reports-container {
  margin-top: 20px;
  padding: 40px;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #101828;
  margin: 0 0 20px 0;
}

.filters-row {
  display: flex;
  gap: 15px;
  align-items: center;
  margin-bottom: 30px;
}

.btn-filter-wrapper {
  display: flex;
  gap: 10px;
  align-items: center;
}

.btn-filter:hover {
  background: #f9fafb;
  transform: translateY(-1px);
}

.btn-filter:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-filter.loading:hover {
  background: #ffffff;
  transform: none;
}

@keyframes miniSpin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 200px;
  gap: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.reports-cards-grid {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.report-card {
  width: 100%;
  height: 100%;
  border: 1px solid #EAECF0;
  background-color: #fff;
  border-radius: 20px;
  padding: 15px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  position: relative;
}

.new-report-highlight {
  animation: newReportPulse 3s ease-in-out;
  border: 2px solid #22c55e !important;
  box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.1);
}

@keyframes newReportPulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4);
  }
  50% {
    transform: scale(1.02);
    box-shadow: 0 0 0 8px rgba(34, 197, 94, 0.1);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
  }
}

.status-tag {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #040C2D;
  background-color: #DDFBDE;
  border-radius: 8px;
  padding: 3px 15px;
  width: fit-content;
}

.status-dot {
  height: 8px;
  width: 8px;
  border-radius: 50%;
  background-color: #3DA33E;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.report-date {
  display: block;
  font-size: 10px;
  color: #667085;
  margin-top: 30px;
}

.report-title {
  font-size: 24px;
  color: #040C2D;
  margin-bottom: 30px;
}

.report-type-tag {
  background-color: #F2F4F7;
  padding: 3px 15px;
  border-radius: 8px;
  font-size: 10px;
  color: #040C2D;
  width: fit-content;
}

.card-actions {
  position: absolute;
  top: 15px;
  right: 15px;
}

.action-button {
  background: #F5F5F5;
  border: none;
  cursor: pointer;
  height: 24px;
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px !important;
  transition: all 0.2s ease;
}

.action-button:hover {
  background: #E5E5E5;
  transform: scale(1.05);
  border: 1px solid #E5E5E5;
}

.action-button svg {
  width: 24px !important;
  height: 16px !important;
}

.empty-state-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
}

@media (max-width: 992px) {
  .reports-cards-grid {
    grid-template-columns: 1fr;
  }

  .filters-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
}
</style>
